import React, { useState, useEffect, useRef, useMemo } from 'react';
import BoardSVG from './components/BoardSVG';
import Dice from './components/Dice';
import Token from './components/Token';
import LudoOverlay from './components/LudoOverlay';
import RoomControls from '../../common/RoomControls';
import PlayerLabel from '../../common/PlayerLabel';
import BrandFooter from '../../common/BrandFooter';
import useAudio from '../../UseAudio';
import { AnimatePresence, motion } from 'framer-motion';
import '../BoardRoom.css';

export default function LudoBoard({ gameState: gs, myPlayerId, send, onLeave, isSpectator }) {
  const { playSound } = useAudio();
  const [isRolling, setIsRolling] = useState(false);
  const isWaitingForRoll = useRef(false);
  const [notification, setNotification] = useState(null);
  
  const players = useMemo(() => gs.players || [], [gs.players]);
  const myIdx = gs.your_index;
  const isMyTurn = !isSpectator && gs.current_turn === myIdx;
  const canRoll = isMyTurn && gs.phase === 'rolling';
  const isHost = gs.host_id === myPlayerId;

  console.log(`[LUDO] Render Phase: ${gs.phase}, Turn: ${gs.current_turn}, Dice: ${gs.dice_value}, isMyTurn: ${isMyTurn}`);

  // Track state changes for notifications
  const prevTokens = useRef([]);

  useEffect(() => {
    // If we were waiting for a roll and the backend updated the dice value, stop local animation
    if (isWaitingForRoll.current && gs.dice_value > 0) {
      setIsRolling(false);
      isWaitingForRoll.current = false;
      playSound('cardPlay');
      
      if (gs.dice_value === 6) {
        showNotification("🎲 ROLLED A 6! BONUS TURN!");
      }
    }

    // Failsafe cleanup
    if (gs.phase === 'rolling' && !isWaitingForRoll.current && isRolling) {
      setIsRolling(false);
    }
  }, [gs.dice_value, gs.phase, isRolling, playSound]);

  useEffect(() => {
    // Stop rolling if turn changes unexpectedly
    if (gs.current_turn !== myIdx && isRolling) {
      setIsRolling(false);
      isWaitingForRoll.current = false;
    }
  }, [gs.current_turn, myIdx, isRolling]);

  useEffect(() => {
    // Detect captures
    const currentTokens = players.flatMap(p => p?.tokens || []);
    if (prevTokens.current.length > 0) {
      currentTokens.forEach((t) => {
        const prevT = prevTokens.current.find(pt => pt.id === t.id && pt.player_index === t.player_index);
        if (prevT && prevT.state === 'ACTIVE' && t.state === 'BASE') {
          showNotification(`⚔️ ${players[t.player_index]?.name}'S TOKEN CAPTURED!`);
          playSound('trumpReveal'); 
        }
      });
    }
    prevTokens.current = currentTokens;
  }, [gs.players, players, playSound]);

  const showNotification = (text) => {
    setNotification(text);
    setTimeout(() => setNotification(null), 3000);
  };

  const handleRoll = () => {
    if (canRoll && !isRolling) {
      setIsRolling(true);
      isWaitingForRoll.current = true;
      playSound('diceRoll'); 
      // Fast roll delay
      setTimeout(() => {
        send('roll_die', {});
      }, 400);
    }
  };

  const getLabelPos = (index) => {
    switch (index) {
      case 0: return 'ludo-label-red';    // Top-Left
      case 1: return 'ludo-label-green';  // Top-Right
      case 2: return 'ludo-label-yellow'; // Bottom-Right
      case 3: return 'ludo-label-blue';   // Bottom-Left
      default: return '';
    }
  };

  const getDicePos = (index) => {
    switch (index) {
      case 0: return { top: '22%', left: '10%' };    // Red
      case 1: return { top: '22%', right: '10%' };   // Green
      case 2: return { bottom: '22%', right: '10%' }; // Yellow
      case 3: return { bottom: '22%', left: '10%' };   // Blue
      default: return { top: '50%', left: '50%' };
    }
  };

  const renderTokens = () => {
    const tokens = [];
    players.forEach((p, pIdx) => {
      if (!p || !p.tokens) return;
      p.tokens.forEach((t, tIdx) => {
        const isPlayable = isMyTurn && gs.phase === 'moving' && gs.valid_moves?.some(m => m.token_id === t.id);
        tokens.push(
          <Token 
            key={`token-${pIdx}-${tIdx}`} 
            token={t} 
            playerIndex={pIdx} 
            gs={gs}
            isPlayable={isPlayable}
            rollValue={gs.dice_value}
            onClick={() => {
              console.log(`[LUDO] Token clicked: ID ${t.id} for player ${pIdx}`);
              send('move_token', { token_id: t.id });
            }}
          />
        );
      });
    });
    return tokens;
  };

  return (
    <div className="ludo-game-container">
      <RoomControls onLeave={onLeave} />
      
      <div className="ludo-board-wrapper">
        <BoardSVG />
        <div className="ludo-interaction-layer" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
          {renderTokens()}
        </div>

        <AnimatePresence>
          {notification && (
            <motion.div 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -20, opacity: 0 }}
              style={{
                position: 'absolute',
                top: '20%',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 500,
                background: 'rgba(0,0,0,0.8)',
                color: 'var(--neon-blue)',
                padding: '10px 30px',
                borderRadius: '20px',
                fontWeight: 'bold',
                border: '1px solid var(--neon-blue)',
                pointerEvents: 'none',
                whiteSpace: 'nowrap'
              }}
            >
              {notification}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Dynamic Dice Placement near active player */}
      <motion.div 
        layout
        style={{
          position: 'absolute',
          ...getDicePos(gs.current_turn),
          zIndex: 200,
          background: 'rgba(0,0,0,0.3)',
          padding: '10px',
          borderRadius: '12px',
          backdropFilter: 'blur(5px)',
          border: '1px solid rgba(255,255,255,0.1)',
          transition: 'all 0.5s ease'
        }}
      >
        <Dice 
          value={gs.dice_value} 
          rolling={isRolling} 
          onClick={handleRoll}
          disabled={!canRoll}
        />
      </motion.div>

      <div className="ludo-player-labels">
        {players.map((p, i) => (
          p && (
            <div key={`player-${i}`} className={`ludo-label-container ${getLabelPos(i)}`}>
              <PlayerLabel 
                player={p} 
                playerId={i} 
                gs={gs} 
                myId={myIdx} 
                send={send} 
                isSpectator={isSpectator}
              />
              {gs.current_turn === i && <div className="turn-glow" style={{
                position: 'absolute',
                top: -10, left: -10, right: -10, bottom: -10,
                boxShadow: '0 0 20px var(--neon-blue)',
                borderRadius: '50%',
                opacity: 0.5,
                pointerEvents: 'none'
              }} />}
            </div>
          )
        ))}
      </div>

      <LudoOverlay 
        phase={gs.phase} 
        rankings={gs.rankings} 
        players={players} 
        isHost={isHost}
        onNewMatch={() => send('new_match', {})}
      />

      <BrandFooter />
    </div>
  );
}
